// O QUE O LOGIN DIZ QUANDO DÁ ERRADO — `T8.15` e `T8.31`.
//
// Medido no código em 25/09/2026: `login.tsx` tinha **14 chamadas de `alert()`**, e elas eram a
// única forma de retorno da tela. O achado do relatório é literal — *"Senha errada mostra um
// `alert()` do navegador. A recuperação faz o mesmo"* — e a sugestão nomeia o conserto: usar o
// modal que o site já tem, e **mostrar erros junto ao campo**.
//
// Das duas coisas, a segunda é a que importa mais, e o relatório a escreveu como emenda: *"o erro
// tem que aparecer JUNTO AO CAMPO (não num diálogo)"*. Um diálogo obriga a pessoa a ler, fechar e
// só então voltar ao campo — e o campo que causou o erro não está marcado em lugar nenhum. Trocar
// `alert()` por modal bonito conserta a identidade visual e **mantém** esse defeito.
//
// Por isso este módulo não devolve só texto: devolve **de qual campo é o erro**. É a diferença
// entre uma mensagem e uma mensagem que aponta.
//
// ── POR QUE NÃO SE DISPARA UM `alert()` PARA MEDIR ISTO ──────────────────────────────────────
//
// Um diálogo nativo BLOQUEIA a página e, com ela, a sessão de automação inteira: o navegador para
// de aceitar comando e a medição morre no meio. Então o `alert()` foi contado no código e a
// estrutura foi medida na tela — nunca provocado. O relatório diz a mesma coisa pelo lado do
// usuário: *"travam a página"*.

/** Onde a mensagem deve aparecer. `null` = acima do formulário, porque não é de um campo só. */
export type Campo = "usuario" | "senha" | "email" | "novo-email" | null;

export interface Recado {
  campo: Campo;
  /** `erro` pinta e marca `aria-invalid`; `aviso` é informação que não é falha. */
  tom: "erro" | "aviso";
  texto: string;
  /**
   * `true` quando o caminho é deliberadamente indistinguível do sucesso.
   * Só a recuperação usa isto, e o motivo está em `recadoDeRecuperacao`.
   */
  /** T26.R2 · o e-mail não confirmado oferece reenviar a confirmação ali mesmo. */
  reenviavel?: boolean;
  fingeSucesso?: boolean;
}

/**
 * T29.5 · O recado de trocar o e-mail de uma conta não confirmada. A frase de sucesso diz para
 * onde o link foi e o que acontece ao clicar, porque é a única coisa que a pessoa precisa fazer.
 */
export function recadoDaTroca(codigo: string, novoEmail = ""): Recado {
  switch (codigo) {
    case "":
      return {
        campo: null,
        tom: "aviso",
        texto: `Mandamos o link de confirmação para ${novoEmail}. Clique nele: o e-mail da sua conta passa a ser esse, já confirmado, e aí é só entrar com ele e a mesma senha.`,
      };
    case "auth/invalid-email":
    case "auth/missing-email":
      return { campo: "novo-email", tom: "erro", texto: "Esse e-mail novo não parece completo. Confira se tem o @ e o ponto." };
    case "auth/email-already-in-use":
      return {
        campo: "novo-email",
        tom: "erro",
        texto: "Esse e-mail já é de outra conta. Entre com ele, ou escolha outro endereço.",
      };
    case "auth/email-ja-confirmado":
      return { campo: null, tom: "aviso", texto: "O e-mail desta conta já foi confirmado. É só entrar." };
    case "auth/too-many-requests":
      return {
        campo: null,
        tom: "erro",
        texto: "Foram muitas tentativas seguidas. Espere alguns minutos e tente de novo.",
      };
    default:
      return recadoDeEntrada(codigo);
  }
}

/**
 * O recado da tentativa de ENTRAR.
 *
 * A regra que decide o campo: erro de formato ou de existência do e-mail aponta o campo de
 * usuário; credencial errada NÃO aponta nenhum dos dois — dizer "a senha está errada" confirma que
 * o usuário existe, e isso entrega a lista de contas a quem estiver testando endereços.
 */
export function recadoDeEntrada(codigo: string): Recado {
  switch (codigo) {
    case "auth/email-not-verified":
      return {
        campo: null,
        tom: "aviso",
        texto:
          "A sua conta existe, mas o e-mail ainda não foi confirmado. Abra a mensagem que enviamos e clique no link. Não chegou? Confira a caixa de spam, ou peça de novo aqui embaixo.",
        reenviavel: true,
      };
    case "auth/missing-email":
      return { campo: "usuario", tom: "erro", texto: "Preencha o seu e-mail." };
    case "auth/invalid-email":
      return {
        campo: "usuario",
        tom: "erro",
        texto: "Esse e-mail não parece completo. Confira se tem o @ e o ponto.",
      };
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-login-credentials":
      // UM RECADO SÓ PARA OS TRÊS CÓDIGOS, de propósito, e sem apontar campo: distinguir
      // "não existe" de "senha errada" diz a quem testa endereços quais contas existem.
      return {
        campo: null,
        tom: "erro",
        texto: "E-mail ou senha não conferem. Confira os dois e tente de novo.",
      };
    case "auth/too-many-requests":
      return {
        campo: null,
        tom: "erro",
        texto: "Foram muitas tentativas seguidas. Espere alguns minutos e tente de novo.",
      };
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      // A pessoa fechou a janela do Google. Não é falha, e dizer que é assusta sem motivo.
      return { campo: null, tom: "aviso", texto: "" };
    case "auth/network-request-failed":
      return {
        campo: null,
        tom: "erro",
        texto: "Não deu para falar com o servidor. Confira a internet e tente de novo.",
      };
    default:
      return {
        campo: null,
        tom: "erro",
        texto: "Não deu para entrar agora. Tente de novo em instantes.",
      };
  }
}

/** O que a tela diz quando o e-mail de recuperação SAI. */
export const RECUPERACAO_ENVIADA: Recado = {
  campo: null,
  tom: "aviso",
  // ERA "E-mail de verificação enviado", e verificação é outra coisa: é o e-mail que confirma uma
  // conta nova. Quem clicou em "esqueci a senha" e leu aquilo tinha razão de procurar a senha numa
  // mensagem que não a traz.
  // T9.20 — ERA "Enviamos um e-mail com o link…", e a plataforma não tem como saber se
  // enviou: o serviço de contas responde igual para endereço que existe e para endereço que
  // não existe, e essa igualdade é deliberada (ver o comentário de `recadoDeRecuperacao`).
  // Afirmar o envio era a única parte mentirosa, e ela sai sem revelar nada: a frase agora diz
  // a CONDIÇÃO, que é verdadeira nos dois casos.
  texto:
    "Se existe uma conta com esse e-mail, o link para criar uma senha nova está a caminho. " +
    "Confira também o lixo eletrônico.",
};

/**
 * O recado da RECUPERAÇÃO de senha.
 *
 * `auth/user-not-found` devolve **o mesmo recado do sucesso**, e isso não é descuido: responder
 * "esse e-mail não tem conta" transforma a tela de recuperação num verificador de cadastro — quem
 * quiser saber se uma pessoa usa a plataforma só precisa digitar o endereço dela. A tela original
 * já fazia isso, com um `sleep(3000)` para igualar até o tempo de resposta, e a decisão fica
 * preservada aqui com o motivo escrito.
 */
export function recadoDeRecuperacao(codigo: string): Recado {
  switch (codigo) {
    case "auth/missing-email":
      return { campo: "email", tom: "erro", texto: "Preencha o seu e-mail." };
    case "auth/invalid-email":
      return {
        campo: "email",
        tom: "erro",
        texto: "Esse e-mail não parece completo. Confira se tem o @ e o ponto.",
      };
    case "auth/too-many-requests":
      return {
        campo: null,
        tom: "erro",
        texto: "Foram muitos pedidos seguidos. Espere alguns minutos e tente de novo.",
      };
    case "auth/user-not-found":
      return { ...RECUPERACAO_ENVIADA, fingeSucesso: true };
    default:
      return {
        campo: null,
        tom: "erro",
        texto: "Não deu para enviar o e-mail agora. Tente de novo em instantes.",
      };
  }
}
